// src/components/LegalPageLayout.tsx
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { COMPANY } from '@/lib/company-info';
import { useLanguage } from '@/hooks/useLanguage';
import { localePath } from '@/i18n/paths';
import type { LegalDocument } from '@/lib/legal-content';

interface LegalPageLayoutProps {
  document: LegalDocument;
}

const backLinkText = {
  pl: 'Powrót do strony głównej',
  en: 'Back to homepage',
  uk: 'Повернутися на головну',
} as const;

export function LegalPageLayout({ document }: LegalPageLayoutProps) {
  const lang = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Back link */}
        <Link
          to={localePath('/', lang)}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ChevronLeft className="size-4 mr-1" />
          {backLinkText[lang]}
        </Link>

        {/* Header */}
        <header className="mb-8">
          <h1 className="text-3xl font-semibold">{document.title}</h1>
          <p className="text-muted-foreground mt-2">{document.subtitle}</p>
          <p className="text-sm text-muted-foreground mt-1">{document.lastUpdated}</p>
        </header>

        {/* Content */}
        <div className="bg-card rounded-xl shadow-lg ring-1 ring-border p-6">
          {document.sections.map((section, idx) => (
            <div key={section.title} className={idx > 0 ? 'mt-8 border-t pt-8' : ''}>
              <h2 className="text-xl font-semibold mb-4">{section.title}</h2>
              <div className="text-muted-foreground whitespace-pre-line">{section.content}</div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <footer className="mt-8 text-center text-sm text-muted-foreground">
          <p>{COMPANY.name}</p>
          <p>{COMPANY.address}</p>
          <p>
            NIP: {COMPANY.nip} | REGON: {COMPANY.regon}
          </p>
        </footer>
      </div>
    </div>
  );
}
